import { createContext, useContext, useEffect, useMemo, useState } from "react";

const CartContext = createContext(null);

const STORAGE_KEY = "big_pizza_cart_v1";

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });
  const [isOpen, setIsOpen] = useState(false);
  const [promoMode, setPromoMode] = useState(null);
  const clearPromoMode = () => setPromoMode(null);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const addItem = (product, tamano = null, override = null) => {
    const key = override?.key || (tamano ? `${product.id}::${tamano}` : product.id);
    const precio = override?.precioUnitario
      ?? (tamano === "chica" ? product.precioChica
        : tamano === "grande" ? product.precioGrande
        : product.precio);

    setItems((prev) => {
      const idx = prev.findIndex((i) => i.key === key);
      if (idx >= 0) {
        const copy = [...prev];
        copy[idx] = { ...copy[idx], cantidad: copy[idx].cantidad + 1 };
        return copy;
      }
      return [
        ...prev,
        {
          key,
          productoId: product.id,
          nombre: override?.nombre || product.nombre + (tamano ? ` (${tamano === "grande" ? "Grande" : "Chica"})` : ""),
          tamano: tamano,
          precioUnitario: precio,
          precioBase: override?.precioBase ?? null,
          extrasDetalle: override?.extrasDetalle || [],
          imagen: product.imagen,
          cantidad: 1,
          personalizacion: override?.personalizacion || null,
        },
      ];
    });
  };

  const increment = (key) =>
    setItems((prev) => prev.map((i) => (i.key === key ? { ...i, cantidad: i.cantidad + 1 } : i)));
  const decrement = (key) =>
    setItems((prev) =>
      prev
        .map((i) => (i.key === key ? { ...i, cantidad: i.cantidad - 1 } : i))
        .filter((i) => i.cantidad > 0),
    );
  const remove = (key) => setItems((prev) => prev.filter((i) => i.key !== key));
  const clear = () => setItems([]);

  const subtotal = useMemo(
    () => items.reduce((s, i) => s + i.precioUnitario * i.cantidad, 0),
    [items],
  );
  const count = useMemo(() => items.reduce((s, i) => s + i.cantidad, 0), [items]);

  const value = {
    items, subtotal, count,
    addItem, increment, decrement, remove, clear,
    isOpen, setIsOpen,
    promoMode, setPromoMode, clearPromoMode,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be inside CartProvider");
  return ctx;
};
